import { AppShell } from '@/components/layout/app-shell';
import { useProfile } from '@/stores/profile';
import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router';

const TodayRoute = lazy(() => import('@/routes/today/today'));
const SparkRoute = lazy(() => import('@/routes/spark/spark'));
const NotesIndex = lazy(() => import('@/routes/notes/notes-index'));
const NotesDetail = lazy(() => import('@/routes/notes/notes-detail'));
const RevisionDetail = lazy(() => import('@/routes/revision/revision-detail'));
const PyqRoute = lazy(() => import('@/routes/pyq/pyq'));
const TutorRoute = lazy(() => import('@/routes/tutor/tutor'));
const DrillRoute = lazy(() => import('@/routes/drill/drill'));
const AnalyticsRoute = lazy(() => import('@/routes/analytics/analytics'));
const SettingsRoute = lazy(() => import('@/routes/settings/settings'));

const OnboardingWelcome = lazy(() => import('@/routes/onboarding/welcome'));
const OnboardingInterest = lazy(() => import('@/routes/onboarding/interest'));
const OnboardingGoal = lazy(() => import('@/routes/onboarding/goal'));
const OnboardingDiagnostic = lazy(() => import('@/routes/onboarding/diagnostic'));
const OnboardingDone = lazy(() => import('@/routes/onboarding/done'));

/**
 * Anonymous students (coldStartDoneAt === null) land on Spark; signed-up
 * students land on Today. Per product/design/student-personalization.md § 10
 * + spark-feed-freemium-research.md §1: Spark is the front door, Today is
 * the spine. Both stay one tap away once the student has chosen.
 */
function RootRedirect() {
  const coldStartDone = useProfile((s) => s.coldStartDoneAt);
  return <Navigate to={coldStartDone ? '/today' : '/spark'} replace />;
}

export const routes: RouteObject[] = [
  {
    path: 'onboarding',
    children: [
      { index: true, element: <Navigate to="/onboarding/welcome" replace /> },
      { path: 'welcome', element: <OnboardingWelcome /> },
      { path: 'interest', element: <OnboardingInterest /> },
      { path: 'goal', element: <OnboardingGoal /> },
      { path: 'diagnostic', element: <OnboardingDiagnostic /> },
      { path: 'done', element: <OnboardingDone /> },
    ],
  },
  {
    element: <AppShell />,
    children: [
      { index: true, element: <RootRedirect /> },
      { path: 'today', element: <TodayRoute /> },
      { path: 'spark', element: <SparkRoute /> },
      { path: 'notes', element: <NotesIndex /> },
      { path: 'notes/:subject/:topic/:name', element: <NotesDetail /> },
      { path: 'revision/:subject/:topic/:name', element: <RevisionDetail /> },
      { path: 'pyq', element: <PyqRoute /> },
      { path: 'tutor', element: <TutorRoute /> },
      { path: 'drill', element: <DrillRoute /> },
      { path: 'analytics', element: <AnalyticsRoute /> },
      { path: 'settings', element: <SettingsRoute /> },
      { path: '*', element: <RootRedirect /> },
    ],
  },
];
